/**
 * Whether vehicle-event toasts (and their beep) are paused right now.
 *
 * While one vehicle is followed, its docked details panel sits exactly where the toasts stack, and
 * every new toast shoves it around. The panel holds a suppression for as long as it is mounted;
 * `VehicleEventListener` still records each event (feed and badge stay right) but shows no toast.
 *
 * Ref-counted and module-level, like `dismissed-store.ts`: no React state, nothing persisted.
 */

let holders = 0;
const listeners = new Set<() => void>();

function notify() {
  for (const listener of listeners) listener();
}

/** Pauses vehicle-event toasts until the returned release is called. Release is idempotent. */
export function suppressVehicleToasts(): () => void {
  holders += 1;
  if (holders === 1) notify();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    holders -= 1;
    if (holders === 0) notify();
  };
}

export function areVehicleToastsSuppressed(): boolean {
  return holders > 0;
}

/** Called whenever suppression switches on or off. Returns an unsubscribe function. */
export function subscribeVehicleToastSuppression(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
